import { useState, useEffect } from "react";
import { formatUnixTimestamp, secondsToHms } from "services/utils";
import { Player, controls } from "react-media-player";
import { usePodcastProvider, ModalType } from "components/podcast/PodcastProvider";
import { useMediaSource } from "hooks";
import { getPodcastThemeToken } from "services/utils/getPodcastThemeToken";
//ui
import { Button, Box, Flex, Text, IconButton, useMediaQuery, Stack } from "@chakra-ui/react";
import { VolumeIcon, ShareIcon } from "components/icons";
import { CustomPlayPause, CustomMediaSlider, CustomVolumeSlider } from "components/podcast/CustomMediaPlayer";
import PodcastColorTypes from "types/podcastColorTypes";
import { ExpandedScreenModal } from "../ExpandedScreenModal";

export function VideoModalContent() {
  const { playingPodcast, isPlaying, setModalType, setModalPodcast, expandedScreenModal } = usePodcastProvider();
  const { CurrentTime } = controls;
  const [showVolume, setShowVolume] = useState<boolean>(false);
  const [playerSrc, setPlayerSrc] = useState("");

  const { startPlaying } = useMediaSource({ podcastDetails: playingPodcast });
  const [isLargerThan766] = useMediaQuery("(min-width: 766px)");

  const publishDate = playingPodcast?.created ? formatUnixTimestamp(playingPodcast.created) : "";

  const playerBorderRadius = isLargerThan766 ? "16px" : "0px";

  const duration = playingPodcast ? secondsToHms(playingPodcast?.length) : 0;

  useEffect(() => {
    if (playingPodcast?.video_urls) {
      const source = startPlaying();
      setPlayerSrc(source);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playingPodcast]);

  const changeModalType = (type: ModalType) => {
    setModalType(type);
    setModalPodcast(playingPodcast);
  };

  return (
    <Flex maxWidth="1095px" width="100%" mx={{ base: "0px", md: "20px" }} flexDir="column" fontSize="16px" lineHeight="20px" letterSpacing="-0.01em" color="#4A4A4A">
      <Flex width="100%" flexDir="column" alignItems="center" display={{ base: "flex", md: "none" }}>
        <Text fontSize="24px" mb="38px" lineHeight="32px" letterSpacing="-0.02em" fontWeight="600" px={{ base: "39px", md: "0" }} color="purple.50">
          {playingPodcast?.title}
        </Text>
      </Flex>
      <Flex
        fontSize="12px"
        width="100%"
        mt={{ base: "39px", md: "0" }}
        px={{ base: "39px", md: "0" }}
        lineHeight="16px"
        letterSpacing="0.01em"
        justifyContent="space-between"
        color="purple.50"
        display={{ base: "none", md: "flex" }}
      >
        <Flex
          px="8px"
          py="4px"
          bg="green.50"
          borderRadius="8px"
          color={getPodcastThemeToken(PodcastColorTypes.EpisodeModalText)}
          bgColor={getPodcastThemeToken(PodcastColorTypes.EpisodeModalBadgeOne)}
        >
          Episode {playingPodcast?.episode_number}
        </Flex>
        <Flex
          px="8px"
          py="4px"
          ml="22px"
          bg="blue.90"
          borderRadius="8px"
          color={getPodcastThemeToken(PodcastColorTypes.EpisodeModalText)}
          bgColor={getPodcastThemeToken(PodcastColorTypes.EpisodeModalBadgeTwo)}
        >
          {publishDate}
        </Flex>
      </Flex>
      <Flex pt="32px" pb="22px" width="100%" justifyContent={{ base: "flex-start", md: "space-between" }} flexDirection={{ base: "column", md: "row" }}>
        <Flex maxWidth={{ base: "100%", md: "33%" }} width="100%" flexDir="column" mr="20px" display={{ base: "none", md: "flex" }}>
          <Text fontSize="32px" lineHeight="40px" fontWeight="600" as="h5" display={{ base: "none", md: "flex" }}>
            {playingPodcast?.title}
          </Text>
          <Text fontSize="16px" lineHeight="20px">
            {playingPodcast?.description}
          </Text>
          <Text mt="34px" textDecoration="underline">
            View the transcript
          </Text>
          <Text fontSize="16px" lineHeight="22px" mt="20px" color="purple.100" display={{ base: "flex", md: "none" }}>
            {playingPodcast?.important_details}
          </Text>
          <Flex mt={{ base: "28px", md: "24px" }} width={{ base: "100%", md: "auto" }} justifyContent={{ base: "center", md: "flex-start" }}>
            <IconButton
              icon={<ShareIcon width={{ base: "32px" }} height={{ base: "32px" }} />}
              aria-label="share-podcast-link"
              bg="blue.90"
              height="48px"
              width="48px"
              borderRadius={{ base: "48px", md: "48px" }}
              _hover={{ bg: "transparent" }}
              color="purple.50"
              onClick={() => {
                changeModalType(ModalType.Share);
              }}
            />
          </Flex>
        </Flex>
        <Flex maxWidth={{ base: "100%", md: "54%" }} width="100%" flexDir="column" alignItems="flex-end">
          <Flex width="100%" position="relative">
            {!expandedScreenModal && <Player src={playerSrc} vendor="video" style={{ borderRadius: playerBorderRadius, height: "404px !important" }} autoPlay={true} className="media-player"></Player>}

            <Flex position="absolute" width="100%" height="100%" display={{ base: "none", md: "flex" }}>
              <Flex position="relative" width="100%" height="100%" justifyContent="center" alignItems="center">
                <CustomPlayPause expandedPlay={true} />
                <Flex justifyContent="space-between" alignItems="flex-end" position="absolute" width="100%" bottom="20px">
                  <Box>
                    {showVolume && <CustomVolumeSlider volumeSliderHeight="32" />}
                    <Button
                      bgColor="transparent"
                      width="48px"
                      height="48px"
                      color="white"
                      _hover={{ bg: "transparent", color: "purple.50" }}
                      _focus={{ outline: "none", bg: "transparent" }}
                      mt="15px"
                      onClick={() => setShowVolume(!showVolume)}
                    >
                      <VolumeIcon width="48px" height="48px" />
                    </Button>
                  </Box>
                  <ExpandedScreenModal playerSrc={playerSrc} />
                </Flex>
              </Flex>
            </Flex>
            {isPlaying ? (
              <Flex position="absolute" width="100%" height="100%" display={{ base: "flex", md: "none" }}>
                <Flex position="relative" width="100%" height="100%" flexDir="column" justifyContent="center" alignItems="center">
                  <CustomPlayPause expandedPlay={true} />
                  <Flex zIndex="10" px="8px" width="auto" color="white" marginTop="23px" py="4px" bgColor="rgba(137, 137, 199, 0.2)" borderRadius="8px">
                    Click here to start listening
                  </Flex>
                </Flex>
              </Flex>
            ) : (
              <Flex position="absolute" width="100%" height="100%" alignItems="flex-end" display={{ base: "flex", md: "none" }}>
                <Stack direction="row" justifyContent="center" spacing="20px" px="8px" py="4px" width="100%" alignItems="center">
                  <CustomPlayPause size="32px" expandedPlay={true} />
                  {!expandedScreenModal && <CustomMediaSlider detailSmallScreens={true} />}
                  <ExpandedScreenModal playerSrc={playerSrc} />
                </Stack>
              </Flex>
            )}
          </Flex>

          <Flex
            fontSize="12px"
            width="100%"
            mt={{ base: "39px", md: "0" }}
            px={{ base: "39px", md: "0" }}
            lineHeight="16px"
            letterSpacing="0.01em"
            justifyContent="space-between"
            color="purple.50"
            display={{ base: "flex", md: "none" }}
          >
            <Flex px="8px" py="4px" bg="green.50" borderRadius="8px">
              Episode {playingPodcast?.episode_number}
            </Flex>
            <Flex px="8px" py="4px" ml="22px" bg="blue.90" borderRadius="8px">
              {publishDate}
            </Flex>
          </Flex>
          <Flex maxWidth={{ base: "100%", md: "33%" }} px={{ base: "39px", md: "0" }} width="100%" flexDir="column" display={{ base: "flex", md: "none" }}>
            <Text fontSize="16px" lineHeight="20px">
              {playingPodcast?.description}
            </Text>
            <Text mt="34px" textDecoration="underline">
              View the transcript
            </Text>
            <Text fontSize="16px" lineHeight="22px" mt="20px" color="purple.100" display={{ base: "flex", md: "none" }}>
              {playingPodcast?.important_details}
            </Text>
            <Flex mt={{ base: "28px", md: "24px" }} width={{ base: "100%", md: "auto" }} justifyContent={{ base: "center", md: "flex-start" }}>
              <IconButton
                icon={<ShareIcon width={{ base: "32px" }} height={{ base: "32px" }} />}
                aria-label="share-podcast-link"
                bg="blue.90"
                height="48px"
                width="48px"
                borderRadius={{ base: "48px", md: "48px" }}
                _hover={{ bg: "transparent" }}
                color="purple.50"
                onClick={() => {
                  changeModalType(ModalType.Share);
                }}
              />
            </Flex>
          </Flex>
          <Flex pt="28px" width="100%" display={{ base: "none", md: "flex" }} flexDir="column">
            {!expandedScreenModal && <CustomMediaSlider />}
            <Flex justifyContent="space-between" width="100%" mt="10px" fontSize="12px" letterSpacing="0.01em" lineHeight="16px" fontFamily="sora" color="purple.50">
              <div>
                <CurrentTime />
              </div>

              <div>{duration}</div>
            </Flex>
          </Flex>
        </Flex>
      </Flex>
      <Stack width="100%" flexDir="column" alignItems="center" spacing="8px">
        <Stack width="100%" flexDir="column" alignItems="center" spacing="8px" display={{ base: "none", md: "flex" }}></Stack>
      </Stack>
    </Flex>
  );
}
